import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { DataTableDirective } from 'angular-datatables';
import { FlashMessagesService } from 'flash-messages-angular';
import { Subject } from 'rxjs';
import { DatatablesHelper } from 'src/app/helpers';
import { AdminService } from 'src/app/services/admin.service';
import { AuthService } from 'src/app/services/auth.service';
import { UserService } from 'src/app/services/user.service';
import { IRentwiseCertificateQueryParams } from '../rentwise-certificate/rentwise-certificate.viewmodel';
import { InviteUsersDialogComponent } from '../shared/dialogs/invite-users-dialog/invite-users-dialog.component';
import { UploadUsersDialogComponent } from '../shared/dialogs/upload-users-dialog/upload-users-dialog.component';
import { RouteService } from 'src/app/core/services';

@Component({
  selector: 'app-rentwise-users',
  templateUrl: './rentwise-users.component.html',
  styleUrls: ['./rentwise-users.component.css']
})
export class RentwiseUsersComponent implements OnInit, OnDestroy {
  users: any[] = [];
  loaded = false;

  agencyID?: string;
  userID?: string;
  progressID?: string;

  dtOptions: any = {};

  // We use this trigger because fetching the list of users can take a while,
  // thus we ensure the data is fetched before rendering
  dtTrigger: Subject<any> = new Subject<any>();

  @ViewChild(DataTableDirective, { static: false })
  dtElement!: DataTableDirective;

  constructor(
    public auth: AuthService,
    private userService: UserService,
    private dialog: MatDialog,
    private adminService: AdminService,
    private flashMessage: FlashMessagesService,
    private router: Router,
    private routeService: RouteService
  ) { }

  ngOnInit(): void {
    this.initAgency();
    this.initTable();
    this.loadUsers();
  }

  private initAgency() {
    this.agencyID = this.auth.agencyInfo._id;
  }

  private initTable() {
    this.dtOptions = {
      pagingType: 'full_numbers',
      pageLength: 50,
      // enables horizontal scrolling on datatable
      scrollX: true,

      // Declare the use of the extension in the dom parameter and give a class for the css (can't get the css to apply though so commented out)
      // dom: '<"table-buttons"lBfr>tip',
      dom: 'lBfrtip',

      // Configure the buttons
      buttons: [
        'colvis',
        'excel'
      ]
    };
  }

  private async loadUsers() {
    const query = {
      agencyid: this.auth.agencyInfo._id,
      getprogress: true
    };

    const res: any = await this.userService.getRentwise(query).toPromise();

    this.users = res.users;
    this.loadTable();
  }

  private loadTable() {
    if (!this.loaded) {
      // Calling the DT trigger to manually render the table
      this.dtTrigger.next();
      this.loaded = true;
    }
  }

  ngOnDestroy(): void {
    // Do not forget to unsubscribe the event
    this.dtTrigger.unsubscribe();
  }

  uploadUsers() {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    // Open dialog
    const dialogRef = this.dialog.open(UploadUsersDialogComponent, dialogConfig);

    dialogRef.afterClosed().toPromise().then(async (res: any) => {
      if (!res || !res.users.length) return;

      const users = res.users;

      const agencyID = this.auth.agencyInfo._id;

      this.routeService.scrollToTop();

      // Register Users
      const data: any = await this.adminService.registerMany('RentWise', agencyID, users).toPromise();

      // Reload table data
      await this.loadUsers().catch(() => {});

      // Rerender manually
      this.rerender();

      // Success
      if (data.success) {
        // Display success
        this.flashMessage.show('Users registered.', {
          cssClass: 'alert-success',
          timeout: 3000
        });
      }
      // Error
      else {
        // Display error
        this.flashMessage.show('There was an error saving users', {
          cssClass: 'alert-danger',
          timeout: 3000
        });
      }
    });
  }

  inviteUsers() {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    // Open dialog
    const dialogRef = this.dialog.open(InviteUsersDialogComponent, dialogConfig);

    dialogRef.afterClosed().toPromise().then(async (res: any) => {
      if (res.success) {
        this.routeService.scrollToTop();

        const message = res.invited ? `Invitations sent to ${res.invited} recipients.` : 'Invitations sent.';

        // Display success
        this.flashMessage.show(message, {
          cssClass: 'alert-success',
          timeout: 3000
        });
      }
    });
  }

  private rerender() {
    DatatablesHelper.redrawTable(this.dtElement, this.dtTrigger);
  }

  async deleteAgencyUser(index: number) {
    const user = this.users[index];

    // Assign userID
    this.userID = user._id;
    this.progressID = user.progressID;

    if (!this.agencyID || !this.userID) {
      return;
    }

    // Delete Property
    const data: any = await this.adminService.deregisterCourses(this.agencyID, this.userID).toPromise();

    // Reload table data
    await this.loadUsers().catch(() => {});

    // Rerender manually
    this.rerender();

    // Success
    if (data.success) {
      // Display success
      this.flashMessage.show("User deleted.", {
        cssClass: 'alert-success',
        timeout: 3000
      });
    }
  }

  async viewUserCertificate(index: number) {
    const user = this.users[index];

    const queryParams: IRentwiseCertificateQueryParams = {
      userid: user._id
    };

    await this.router.navigate(['/admin/rentwise-certificate'], { queryParams });
  }

  sortDate(value: any) {
    return value ? +new Date(value) : 0;
  }
}
